import { IkeFieldType } from '@ikegps/core-api';
import type { CollectionModel } from '../collection.data';
import type { CollectionModelBase } from '../collection.base';
import type { CollectionModelField } from '../collection.field';
import { CollectionModelFieldNumber } from '../fields/number.field';
import { CollectionModelFieldOrientation } from '../fields/orientation.field';
import { CollectionModelFieldSubForm } from '../fields/form.field';
import type { FormModel } from '../../form/form';
import type { FormModelField } from '../../form/form.field';
import { IkeModelCollection } from '../collection.model';
import type { Log } from '../../../log';
import type { FormModelFieldSubForm } from '../../form/subform/form.subform';
import type { CollectionModelFieldSubFormInstance } from '../collection.base';
import {
	type IkeCollectionMigrationAction,
	IkeCollectionUpgradeActionFactory,
	type IkeCollectionUpgradeRunner,
} from './collection.upgrade.action';
import { convertNumberToOrientation } from './number.orientation.convert';
import { CollectionModelFieldVector } from '../fields/vector.field';
import { CollectionModelFieldAnchor } from '../fields/anchor.field';
import { CollectionModelFieldSelectList } from '../fields/select.list.field';
import { CollectionModelFieldNestedList } from '../fields/nested.list.field';

export type IkeCollectionUpgradeFailure =
	| IkeCollectionMigrationUpgradeMissing
	| IkeCollectionUpgradeFailureReplace
	| IkeCollectionUpgradeFailureNormalize;

/** Field is missing in target collection. */
export interface IkeCollectionMigrationUpgradeMissing {
	fieldId: string;
	fieldName?: string;
	reason: 'missing';
}

/** Field found in target, but no rule was found to map value. */
export interface IkeCollectionUpgradeFailureReplace {
	fieldId: string;
	fieldName?: string;
	reason: 'replace';
	values: string[];
}

/**
 * Number-field value outside the `[0, 360]` bearing range is being migrated to an Orientation field.
 * Listed values are the source numbers that would be normalized if the upgrade is forced.
 */
export interface IkeCollectionUpgradeFailureNormalize {
	fieldId: string;
	fieldName?: string;
	reason: 'normalize';
	values: string[];
}

export class IkeCollectionUpgrade {
	targetForm: FormModel;
	collections: CollectionModel[];

	validations: { [fieldId: string]: IkeCollectionUpgradeFailure } = {};
	actions: { [fieldId: string]: IkeCollectionUpgradeRunner } = {};

	constructor(collections: CollectionModel[], targetForm: FormModel) {
		this.collections = collections;
		this.targetForm = targetForm;
	}

	/** Add a migration action for a field, eg drop, replace */
	addAction(action: IkeCollectionMigrationAction): this {
		if (this.actions[action.fieldId]) {
			throw new Error('Duplicate action fieldId: ' + action.fieldId);
		}
		this.actions[action.fieldId] = IkeCollectionUpgradeActionFactory.createAction(action);
		return this;
	}

	async process(log: Log) {
		const output: CollectionModel[] = [];

		for (const oldCollection of this.collections) {
			const modelCollection = new IkeModelCollection();
			const newCollection = modelCollection.read(
				await modelCollection.write(oldCollection),
				this.targetForm,
				oldCollection.loader
			);
			await newCollection.ready();

			await this.processSubForm(oldCollection, newCollection, log.child({ collectionId: oldCollection.id }));

			output.push(newCollection);
		}
		const validations = Object.keys(this.validations);
		if (validations.length > 0) {
			return { result: 'failure' as const, fields: validations.map((c) => this.validations[c]!) };
		}
		return { result: 'success' as const, collections: output };
	}

	private findMatchingField(
		field: FormModelField,
		oldSubForm: FormModelFieldSubForm,
		newSubForm: FormModelFieldSubForm
	): string {
		// If the action maps the field then use that
		const action = this.actions[field.id];
		if (action != null) {
			return action.targetFieldId;
		}

		// Attempt to look for a matching field by display name

		const sourceMatches = oldSubForm.fields.filter((f) => f.displayName === field.displayName && f.type === field.type);
		if (sourceMatches.length > 1) {
			// Multiple source fields, skip
			return null;
		}

		const matches = newSubForm.fields.filter((f) => f.displayName === field.displayName && f.type === field.type);
		if (matches.length !== 1) {
			// Multiple target fields, skip
			return null;
		}

		return matches[0].id;
	}

	private getAction(oldField: CollectionModelField) {
		return this.actions[oldField.schema.id];
	}

	private async processSubForm(
		oldSubForm: CollectionModelBase,
		newSubForm: CollectionModelBase,
		log: Log
	): Promise<IkeCollectionUpgradeFailure[]> {
		let failures: IkeCollectionUpgradeFailure[] = [];

		for (const schemaField of oldSubForm.schema.fields) {
			const oldField = oldSubForm.getField(schemaField.id);
			if (!oldField.hasValue()) {
				continue;
			}

			let targetFieldId = schemaField.id;

			const action = this.getAction(oldField);

			// Handle 'drop' as a special case when target form does not have field defined
			if (action != null && !action.requiresTargetField) {
				if (newSubForm.hasField(targetFieldId)) {
					const newDropField = newSubForm.getField(targetFieldId);
					const isActionProcessed = action.process(oldField, newDropField, log);
					if (isActionProcessed) {
						log.trace(
							{ oldFieldId: oldField.id, newFieldId: newDropField.id, action: action.reason },
							'Applied action'
						);
					}
				}

				continue;
			}

			// Attempt to find the mapping for a missed field
			if (!newSubForm.schema.hasField(schemaField.id)) {
				targetFieldId = this.findMatchingField(schemaField, oldSubForm.schema, newSubForm.schema);

				if (targetFieldId == null) {
					log.warn({ fieldId: schemaField.id }, 'Unable to find field mapping');
					this.validations[schemaField.id] = {
						reason: 'missing',
						fieldId: schemaField.id,
						fieldName: schemaField.displayName,
					};
					continue;
				}

				if (oldField.schema.type !== IkeFieldType.FORM) {
					// Form duplication will handle recursively to apply rules at each level
					const targetField = newSubForm.getField(targetFieldId);
					targetField.Mutator.copy(oldField);
				}
			}

			const newField = newSubForm.getField(targetFieldId);

			// field types have changed - only a small whitelist of pairs is supported
			if (newField.schema.type !== oldField.schema.type) {
				const isNumberToOrientation =
					oldField.schema.type === IkeFieldType.NUMBER && newField.schema.type === IkeFieldType.ORIENTATION;
				const isVectorToAnchor =
					oldField.schema.type === IkeFieldType.VECTOR && newField.schema.type === IkeFieldType.ANCHOR;
				const isSelectListToNestedList =
					oldField.schema.type === IkeFieldType.SELECT_LIST && newField.schema.type === IkeFieldType.NESTED_LIST;

				if (!isNumberToOrientation && !isVectorToAnchor && !isSelectListToNestedList) {
					log.error(
						{
							oldFieldId: schemaField.id,
							newFieldId: newField.id,
							oldType: oldField.schema.type,
							newType: newField.schema.type,
						},
						'Mapping invalid types'
					);
					throw new Error('Cannot map between types');
				}
			}

			// Convert NUMBER → ORIENTATION: transfer captures directly, normalize manual values
			if (
				CollectionModelFieldNumber.isNumberField(oldField) &&
				CollectionModelFieldOrientation.isOrientationField(newField)
			) {
				const res = convertNumberToOrientation(oldField, newField);
				if (res.status === 'normalize') {
					this.addNormalizeValue(schemaField, res.sourceValue);
				} else if (res.status === 'dropped-sag') {
					this.addReplaceValue(schemaField, res.sourceDisplay);
				}
				continue;
			}

			// Convert VECTOR → ANCHOR: copy vector details into the anchor field
			if (CollectionModelFieldVector.isVectorField(oldField) && CollectionModelFieldAnchor.isAnchorField(newField)) {
				if (oldField.isPointToPoint()) {
					newField.Mutator.p2p(oldField.stateValue?.captures || [], CollectionModelFieldAnchor.DEFAULT_PLACEMENT);
				} else if (oldField.isManual() || oldField.isCollectionLink()) {
					const { distance, bearing, pitch } = oldField.value;
					newField.Mutator.manual({
						collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
						placement: CollectionModelFieldAnchor.DEFAULT_PLACEMENT,
						distance,
						bearing,
						pitch,
					});
				}
				continue;
			}

			// Convert SELECT_LIST → NESTED_LIST: transfer the selected option text to the first level
			if (
				CollectionModelFieldSelectList.isSelectListField(oldField) &&
				CollectionModelFieldNestedList.isNestedListField(newField)
			) {
				const displayText = oldField.displayValue;
				if (displayText != null) {
					newField.Mutator.manual([displayText]);
					if (!newField.hasValue()) {
						this.addReplaceValue(schemaField, displayText);
					}
				}
				continue;
			}

			// Duplicate a sub form
			if (
				CollectionModelFieldSubForm.isSubFormField(oldField) &&
				CollectionModelFieldSubForm.isSubFormField(newField)
			) {
				for (const oldInstance of oldField.value) {
					const newInstance = this.getOrCreateSubformInstance(newField, oldInstance.instanceId);
					const res = await this.processSubForm(oldInstance, newInstance, log);
					if (res.length > 0) {
						failures = failures.concat(res);
					}
				}
			}

			if (action != null && action.process != null) {
				const isActionProcessed = action.process(oldField, newField, log);
				if (isActionProcessed) {
					log.trace({ oldFieldId: oldField.id, newFieldId: newField.id, action: action.reason }, 'Applied action');
					continue;
				}
			}

			if (newField.displayValue !== oldField.displayValue) {
				// Exception for IPF form upgrades: only if oldFormVersion < 3 and newFormVersion >= 3
				// and the field is serviceCommunicationsCable or servicePowerConductor
				const oldFormVersion = oldSubForm.schema.getForm().metadata.getIkePoleForeman?.()?.formVersion ?? 1;
				const newFormVersion = this.targetForm?.metadata?.getIkePoleForeman?.()?.formVersion;
				const isIPFUpgrade =
					(oldField.schema.id === 'ipf_serviceCommunicationsCable' ||
						oldField.schema.id === 'ipf_servicePowerConductor') &&
					newFormVersion !== undefined &&
					oldFormVersion < 3 &&
					newFormVersion >= 3;
				if (isIPFUpgrade) {
					newField.Mutator.truncatedCopy(oldField);
				} else {
					log.error(
						{
							oldFieldId: schemaField.id,
							oldDisplay: oldField.displayValue,
							newDisplay: newField.displayValue,
						},
						'Display miss match'
					);
					this.addReplaceValue(schemaField, oldField.displayValue);
				}
			}
		}
		return failures;
	}

	private getOrCreateSubformInstance(
		subform: CollectionModelFieldSubForm,
		instanceId: string
	): CollectionModelFieldSubFormInstance {
		const newInstance = subform.getInstance(instanceId);
		if (newInstance != null) {
			return newInstance;
		}

		subform.Mutator.create(instanceId);
		return subform.getInstance(instanceId);
	}

	private addReplaceValue(field: FormModelField, value: string) {
		const existing = this.validations[field.id] || {
			fieldId: field.id,
			fieldName: field.displayName,
			reason: 'replace',
			values: [],
		};
		if (existing.reason !== 'replace') {
			throw new Error('Missing and Replace: ' + field.id);
		}
		if (existing.values.indexOf(value) > -1) {
			return;
		}
		existing.values.push(value);
		this.validations[field.id] = existing;
	}

	private addNormalizeValue(field: FormModelField, value: string) {
		const existing: IkeCollectionUpgradeFailureNormalize = (this.validations[field.id] as
			| IkeCollectionUpgradeFailureNormalize
			| undefined) || {
			fieldId: field.id,
			fieldName: field.displayName,
			reason: 'normalize',
			values: [],
		};
		if (existing.reason !== 'normalize') {
			throw new Error('Mixed validation reasons: ' + field.id);
		}
		if (existing.values.indexOf(value) > -1) {
			return;
		}
		existing.values.push(value);
		this.validations[field.id] = existing;
	}
}
